import { useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

export interface SearchResult {
  id: string
  title: string | null
  excerpt: string | null
  content: string | null
  url: string
  source_type: string | null
  is_read: boolean
  is_favorite: boolean
  is_archived: boolean
  reading_progress: number
  word_count: number | null
  created_at: string
  updated_at: string
  rank: number
  reading_time: number
}

export interface SearchFilters {
  isRead?: boolean
  isFavorite?: boolean
  sourceType?: string
  dateFrom?: string
  dateTo?: string
}

interface UseSearchReturn {
  query: string
  setQuery: (query: string) => void
  results: SearchResult[]
  loading: boolean
  error: string | null
  hasSearched: boolean
  debouncedSearch: (query: string, filters?: SearchFilters, delay?: number) => void
  clearSearch: () => void
}

export function useSearch(userId: string | undefined): UseSearchReturn {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const search = useCallback(async (searchQuery: string, filters: SearchFilters = {}) => {
    if (!userId || !searchQuery.trim()) {
      setResults([])
      setHasSearched(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Call the search_articles function
      const { data, error: searchError } = await supabase
        .rpc('search_articles', {
          search_query: searchQuery.trim(),
          user_uuid: userId,
          result_limit: 50
        })

      if (searchError) throw searchError

      // Apply client-side filters
      let filteredResults = (data || []) as SearchResult[]

      // Calculate reading time (avg 200 words per minute)
      filteredResults = filteredResults.map(r => ({
        ...r,
        reading_time: r.word_count ? Math.ceil(r.word_count / 200) : 0
      }))

      // Filter by read status
      if (filters.isRead !== undefined) {
        filteredResults = filteredResults.filter(r => r.is_read === filters.isRead)
      }

      // Filter by favorite
      if (filters.isFavorite) {
        filteredResults = filteredResults.filter(r => r.is_favorite)
      }

      // Filter by source type
      if (filters.sourceType) {
        filteredResults = filteredResults.filter(r => r.source_type === filters.sourceType)
      }

      // Filter by date range
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom)
        filteredResults = filteredResults.filter(r => new Date(r.created_at) >= fromDate)
      }
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo)
        toDate.setHours(23, 59, 59, 999)
        filteredResults = filteredResults.filter(r => new Date(r.created_at) <= toDate)
      }

      // Filter out archived by default
      filteredResults = filteredResults.filter(r => !r.is_archived)

      setResults(filteredResults)
      setHasSearched(true)
    } catch (err) {
      console.error('Search error:', err)
      setError(err instanceof Error ? err.message : 'Search failed')
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [userId])

  const debouncedSearch = useCallback((searchQuery: string, filters: SearchFilters = {}, delay: number = 300) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!searchQuery.trim()) {
      setResults([])
      setHasSearched(false)
      setLoading(false)
      return
    }

    setLoading(true)

    debounceRef.current = setTimeout(() => {
      search(searchQuery, filters)
    }, delay)
  }, [search])

  const clearSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setQuery('')
    setResults([])
    setHasSearched(false)
    setError(null)
    setLoading(false)
  }, [])

  return {
    query,
    setQuery,
    results,
    loading,
    error,
    hasSearched,
    debouncedSearch,
    clearSearch
  }
}
